import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Mail, MapPin } from "lucide-react";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { name: "", contact_name: "", contact_email: "", industry: "", location: "", notes: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => { setLoading(true); api.get("/agency/clients").then((r) => setClients(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Client name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/agency/clients", form);
      setClients((prev) => [data, ...prev]);
      setForm(EMPTY); setOpen(false);
      toast.success("Client added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/agency/clients/${id}`); setClients((prev) => prev.filter((c) => c.id !== id)); toast.success("Client removed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen wo-bg">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 pb-28 lg:pb-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400"><Building2 size={20} /></div>
            <div>
              <h1 className="font-display font-bold text-3xl text-slate-100">Client Companies</h1>
              <p className="text-slate-400 text-sm"><span className="font-mono text-teal-400" data-testid="clients-count">{clients.length}</span> companies you hire for</p>
            </div>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-client-btn" className="bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold hover:opacity-90"><Plus size={16} className="mr-2" /> Add client</Button>
            </DialogTrigger>
            <DialogContent className="!fixed liquid-glass border-white/10 text-slate-100 max-h-[85vh] flex flex-col p-0 gap-0">
              <DialogHeader className="p-6 pb-3"><DialogTitle>Add a client company</DialogTitle>
                <DialogDescription className="text-slate-400 text-sm">The company you recruit for. Jobs and submissions can be attributed to this client.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 px-6 overflow-y-auto flex-1 min-h-0">
                <div><Label className="text-slate-300 text-sm">Company name *</Label><Input data-testid="client-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 bg-slate-950/50 border-white/10 text-slate-100" placeholder="Acme Corp" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-slate-300 text-sm">Contact name</Label><Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="mt-1.5 bg-slate-950/50 border-white/10 text-slate-100" /></div>
                  <div><Label className="text-slate-300 text-sm">Contact email</Label><Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="mt-1.5 bg-slate-950/50 border-white/10 text-slate-100" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-slate-300 text-sm">Industry</Label><Input value={form.industry} onChange={(e) => set("industry", e.target.value)} className="mt-1.5 bg-slate-950/50 border-white/10 text-slate-100" /></div>
                  <div><Label className="text-slate-300 text-sm">Location</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1.5 bg-slate-950/50 border-white/10 text-slate-100" /></div>
                </div>
                <div><Label className="text-slate-300 text-sm">Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1.5 bg-slate-950/50 border-white/10 text-slate-100" /></div>
              </div>
              <DialogFooter className="p-6 pt-3 border-t border-white/10 shrink-0">
                <Button onClick={create} disabled={saving} data-testid="save-client-btn" className="w-full bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold">{saving ? "Saving…" : "Add client"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <p className="text-slate-500 text-center py-20">Loading…</p>
        ) : clients.length === 0 ? (
          <div className="text-center py-24 text-slate-500" data-testid="clients-empty"><Building2 size={40} className="mx-auto text-slate-600 mb-4" /> No client companies yet. Add the companies you recruit for.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {clients.map((c) => (
              <div key={c.id} data-testid="client-card" className="liquid-glass rounded-2xl p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-slate-900 font-bold shrink-0">{c.name?.[0]?.toUpperCase()}</div>
                    <div className="min-w-0"><div className="font-semibold text-slate-100 truncate">{c.name}</div>{c.industry && <div className="text-xs text-slate-400 truncate">{c.industry}</div>}</div>
                  </div>
                  <button onClick={() => remove(c.id)} data-testid="delete-client-btn" className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-white/5"><Trash2 size={15} /></button>
                </div>
                <div className="mt-4 space-y-1.5 text-xs text-slate-400">
                  {c.contact_name && <div>{c.contact_name}</div>}
                  {c.contact_email && <div className="flex items-center gap-1.5"><Mail size={12} /> {c.contact_email}</div>}
                  {c.location && <div className="flex items-center gap-1.5"><MapPin size={12} /> {c.location}</div>}
                </div>
                {c.notes && <p className="mt-3 text-xs text-slate-500 border-t border-white/5 pt-3">{c.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
